import { useEffect, useMemo, useRef, useState } from "react";
import { todayISO, addDaysISO } from "@/lib/format";
import { withKey } from "@/components/invoice-editor/parts";

export const emptyCustomer = { company_name: "", contact: "", street: "", zip: "", city: "", email: "", vat_id: "" };
const emptyItem = { description: "", quantity: 1, unit_price: 0 };

const initialForm = () => ({
  invoiceDate: todayISO(),
  serviceDate: todayISO(),
  serviceDateEnd: "",
  dueDate: addDaysISO(14),
  vatRate: 19,
  klein: false,
  note: "",
  status: "offen",
  refLabel: "",
  reference: "",
});

const fromInvoice = (base, copy, paymentDays) => ({
  invoiceDate: copy ? todayISO() : base.invoice_date || todayISO(),
  serviceDate: copy ? todayISO() : base.service_date || "",
  serviceDateEnd: copy ? "" : base.service_date_end || "",
  dueDate: copy ? addDaysISO(paymentDays ?? 14) : base.due_date || "",
  vatRate: base.vat_rate ?? 19,
  klein: Boolean(base.kleinunternehmer),
  note: base.note || "",
  status: copy ? "offen" : base.status || "offen",
  refLabel: base.reference_label || "",
  reference: base.reference || "",
});

export function validateInvoice(customer, items, f) {
  if (!customer.company_name && !customer.contact) return "Bitte einen Empfänger angeben.";
  if (items.every((i) => !i.description)) return "Bitte mindestens eine Position mit Beschreibung angeben.";
  const missing = items.find((i) => i.description && (i.quantity === "" || i.unit_price === ""));
  if (missing) return `Bitte Menge und Preis ergänzen: ${missing.description}`;
  if (f.serviceDateEnd && f.serviceDate && f.serviceDateEnd < f.serviceDate) return "Das Ende des Leistungszeitraums liegt vor dem Beginn.";
  return null;
}

export function buildPayload(customer, items, f) {
  return {
    customer,
    invoice_date: f.invoiceDate,
    service_date: f.serviceDate,
    service_date_end: f.serviceDateEnd,
    due_date: f.dueDate,
    items: items.map((i) => ({ description: i.description, quantity: Number(i.quantity || 0), unit_price: Number(i.unit_price || 0) })),
    vat_rate: Number(f.vatRate || 0),
    kleinunternehmer: f.klein,
    note: f.note,
    status: f.status,
    reference_label: f.reference ? f.refLabel || "Referenz" : "",
    reference: f.reference,
  };
}

export function useInvoiceForm({ base, isCopy, settings, applyDefaults }) {
  const [customer, setCustomer] = useState(emptyCustomer);
  const [items, setItemsRaw] = useState(() => [withKey(emptyItem)]);
  const [f, setF] = useState(initialForm);
  const loadedFor = useRef(null);
  const seeded = useRef(false);
  const paymentDays = settings?.default_payment_days;

  const set = (k) => (v) => setF((prev) => ({ ...prev, [k]: v }));
  const setItems = (list) => setItemsRaw(list.map((i) => (i._k ? i : withKey(i))));

  useEffect(() => {
    if (!applyDefaults || !settings || seeded.current) return;
    seeded.current = true;
    setF((prev) => ({
      ...prev,
      vatRate: settings.default_vat_rate ?? 19,
      klein: Boolean(settings.kleinunternehmer),
      note: settings.default_note || "",
      dueDate: addDaysISO(settings.default_payment_days ?? 14),
    }));
  }, [applyDefaults, settings]);

  useEffect(() => {
    if (!base || (isCopy && !settings) || loadedFor.current === base.id) return;
    loadedFor.current = base.id;
    setCustomer({ ...emptyCustomer, ...(base.customer || {}) });
    setItemsRaw(base.items?.length ? base.items.map(withKey) : [withKey(emptyItem)]);
    setF(fromInvoice(base, isCopy, paymentDays));
  }, [base, isCopy, paymentDays]);

  const totals = useMemo(() => {
    const net = items.reduce((s, i) => s + Number(i.quantity || 0) * Number(i.unit_price || 0), 0);
    const vatAmount = (net * (f.klein ? 0 : Number(f.vatRate || 0))) / 100;
    return { net, vatAmount, gross: net + vatAmount };
  }, [items, f.klein, f.vatRate]);

  const applyTemplate = (t) => {
    setItems(t ? t.items : [emptyItem]);
    setF((prev) => ({
      ...prev,
      refLabel: t ? t.referenceLabel || "" : "",
      reference: "",
      klein: t ? Boolean(t.klein) : Boolean(settings?.kleinunternehmer),
      vatRate: t ? t.vatRate : settings?.default_vat_rate ?? 19,
      note: t ? t.note : settings?.default_note || "",
    }));
  };

  return { customer, setCustomer, items, setItems, f, set, totals, applyTemplate };
}
